import { createAsyncThunk } from "@reduxjs/toolkit";
import { get } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { mapWorkItemsListResponse } from "../../Pages/Projects/projectMappers";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  buildGlobalWorkItemsQuery,
  buildProjectWorkItemsQuery,
} from "./projectManagementThunkUtils";
import { buildGlobalKanbanFetchParams } from "../../Pages/Projects/project-detail/issues/issues.mapper";
import {
  buildGlobalKanbanColumnsFromBootstrap,
  normalizeWorkItemStatusCategoriesForApi,
} from "../../Pages/Issues/issueMappers";
import { KANBAN_DEFAULT_LIMIT } from "../../constants";

function normalizeKanbanStatusCategoryToken(category) {
  const mapped = normalizeWorkItemStatusCategoriesForApi(category);
  return Array.isArray(mapped) ? mapped[0] : mapped;
}

function parseBootstrapColumns(raw) {
  const data = raw?.data ?? raw;
  const columns = data?.columns ?? raw?.columns ?? [];
  return Array.isArray(columns) ? columns : [];
}

function mapBootstrapColumn(rawColumn) {
  const mapped = mapWorkItemsListResponse({
    data: {
      data: rawColumn?.items ?? [],
      meta: rawColumn?.meta ?? {},
    },
  });
  const meta = mapped.meta ?? {};
  return {
    items: mapped.list ?? [],
    nextCursor: meta.nextCursor ?? null,
    hasMore: meta.hasMore ?? false,
    total: meta.total ?? null,
  };
}

/**
 * Fetch the first Kanban page for every visible column in one request.
 */
export const fetchKanbanBoardBootstrap = createAsyncThunk(
  "kanbanColumns/bootstrap",
  async (arg, { rejectWithValue }) => {
    const {
      boardKey,
      projectId,
      assigneeId,
      limit = KANBAN_DEFAULT_LIMIT,
      columns = [],
      listingFilter,
      search,
    } = arg;

    try {
      const isProjectBoard = Boolean(projectId);
      const params = isProjectBoard
        ? buildProjectWorkItemsQuery({ assigneeId, limit, search })
        : listingFilter && typeof listingFilter === "object"
          ? buildGlobalWorkItemsQuery(
              buildGlobalKanbanFetchParams(listingFilter, { page: 1, limit })
            )
          : buildGlobalWorkItemsQuery({
              page: 1,
              limit,
              search,
              ...(() => {
                const has =
                  (Array.isArray(assigneeId) && assigneeId.length > 0) ||
                  (!Array.isArray(assigneeId) &&
                    assigneeId != null &&
                    String(assigneeId).trim() !== "");
                return has ? { assigneeId } : {};
              })(),
            });
      const response = await get(
        isProjectBoard
          ? API_ENDPOINTS.PROJECTS.GET_KANBAN_INITIAL(projectId)
          : API_ENDPOINTS.WORK_ITEMS.GET_KANBAN_INITIAL,
        params,
        {},
        // One in-flight slot per board — a newer bootstrap (search/filter change, etc.)
        // always cancels an older one, regardless of what params changed, so a slow
        // earlier response can never overwrite a faster later one.
        { requestKey: `kanban-bootstrap:${boardKey}` }
      );

      const rawColumns = parseBootstrapColumns(response.data);
      const columnDefs = isProjectBoard
        ? columns
        : (() => {
            const fromApi = buildGlobalKanbanColumnsFromBootstrap(response.data);
            return fromApi.length ? fromApi : columns;
          })();
      const normalizedColumns = columnDefs.map((column) => {
        const rawColumn = isProjectBoard
          ? rawColumns.find(
              (entry) =>
                normalizeKanbanStatusCategoryToken(entry?.statusCategory) ===
                normalizeKanbanStatusCategoryToken(column.category)
            )
          : rawColumns.find(
              (entry) =>
                normalizeKanbanStatusCategoryToken(entry?.statusCategory) ===
                normalizeKanbanStatusCategoryToken(column.category)
            );
        const mapped = mapBootstrapColumn(rawColumn ?? {});
        return {
          columnKey: column.key,
          ...mapped,
        };
      });

      return {
        boardKey,
        columns: normalizedColumns,
        columnDefs,
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to load board"));
    }
  }
);

/**
 * Fetch one page for a single Kanban column — hits the same bootstrap endpoint
 * (`kanban/initial`) as {@link fetchKanbanBoardBootstrap}, with `page` incremented, and
 * keeps only the one column matching `statusCategories`. Kept on this endpoint (rather than
 * the flat `/work-items` list) because `kanban/initial` is the endpoint that scopes results
 * to Epic work items only — the flat list endpoint does not apply that scope.
 *
 * @param {object} arg
 * @param {string}           arg.boardKey          'global' or projectId
 * @param {string}           arg.columnKey         column.key from buildGlobalKanbanColumns / buildProjectKanbanCategoryColumns
 * @param {boolean}          arg.isLoadMore        false = initial fetch (replaces items), true = append
 * @param {string}           [arg.projectId]       undefined for global board
 * @param {string}           [arg.statusCategories] category token — global and project boards (e.g. 'NEW')
 * @param {string|string[]}  [arg.assigneeId]       optional assignee filter (legacy global board)
 * @param {object}           [arg.listingFilter]    global board — full listing scope (saved filter, etc.),
 *                                                  including `.search` — takes priority over `arg.search`
 * @param {string}           [arg.search]           project board (and global board fallback) keyword search
 * @param {number}           [arg.page]             page number to fetch — default 1
 * @param {number}           [arg.limit]            must match the bootstrap's page size for
 *                                                  offset pagination to stay aligned — default
 *                                                  KANBAN_DEFAULT_LIMIT
 */
export const fetchKanbanColumnPage = createAsyncThunk(
  "kanbanColumns/fetchPage",
  async (arg, { rejectWithValue }) => {
    const {
      boardKey,
      columnKey,
      isLoadMore,
      projectId,
      statusCategories,
      assigneeId,
      listingFilter,
      search,
      page = 1,
      limit = KANBAN_DEFAULT_LIMIT,
    } = arg;

    try {
      const isProjectBoard = Boolean(projectId);
      const params = isProjectBoard
        ? buildProjectWorkItemsQuery({ assigneeId, page, limit, search })
        : listingFilter && typeof listingFilter === "object"
          ? buildGlobalWorkItemsQuery(buildGlobalKanbanFetchParams(listingFilter, { page, limit }))
          : buildGlobalWorkItemsQuery({ page, limit, assigneeId, search });

      const response = await get(
        isProjectBoard
          ? API_ENDPOINTS.PROJECTS.GET_KANBAN_INITIAL(projectId)
          : API_ENDPOINTS.WORK_ITEMS.GET_KANBAN_INITIAL,
        params,
        {},
        // One in-flight slot per column — a newer fetch for this column cancels an older
        // one (e.g. rapid Load More clicks), without affecting other columns' requests.
        { requestKey: `kanban-column-page:${boardKey}:${columnKey}` }
      );

      const normStatusCategories =
        statusCategories != null && String(statusCategories).trim() !== ""
          ? normalizeWorkItemStatusCategoriesForApi(statusCategories)
          : statusCategories;
      const rawColumns = parseBootstrapColumns(response.data);
      const rawColumn = rawColumns.find(
        (entry) =>
          normalizeKanbanStatusCategoryToken(entry?.statusCategory) ===
          normalizeKanbanStatusCategoryToken(normStatusCategories)
      );
      const mapped = mapBootstrapColumn(rawColumn ?? {});

      return {
        boardKey,
        columnKey,
        isLoadMore,
        items: mapped.items,
        nextCursor: mapped.nextCursor,
        hasMore: mapped.hasMore,
        total: mapped.total,
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to load column"));
    }
  }
);
